import {joinOriginAndBasePath} from "./url";

export type CourseDetailsQuery = {
	/** Preserves navigation context (e.g. originating program) for the breadcrumb. */
	program?: string;
	/** Pre-selects a format tab (e.g. "video-based") on the course page. */
	modality?: string;
	/** Pre-selects a campus (Academy campus_id) via `?campus=`. */
	campus?: string;
	/** Pre-selects a sequence part via `?part=` (e.g. "part-1"). */
	part?: string;
};

/**
 * The course-details path (`/course/<slug>`), optionally with `program`,
 * `modality`, `campus`, and/or `part` query params.
 */
export function getCourseDetailsPath(
	slug: string,
	query?: CourseDetailsQuery
): string {
	const path = `/course/${slug}`;
	if (!query) {
		return path;
	}

	const params = new URLSearchParams();
	if (query.program) params.set("program", query.program);
	if (query.modality) params.set("modality", query.modality);
	if (query.campus) params.set("campus", query.campus);
	if (query.part) params.set("part", query.part);

	const qs = params.toString();
	return qs ? `${path}?${qs}` : path;
}

/**
 * The full course-details URL: an origin + base path (e.g. the Discovery
 * site's `/discover`) joined with {@link getCourseDetailsPath}.
 */
export function buildCourseDetailsUrl(
	origin: string,
	basePath: string,
	slug: string,
	query?: CourseDetailsQuery
): string {
	return `${joinOriginAndBasePath(origin, basePath)}${getCourseDetailsPath(slug, query)}`;
}

/**
 * Append a `campus` query parameter to a URL.
 */
export function appendCampusQueryParameter(
	url: string,
	campusId: string | null
): string {
	if (!campusId) {
		return url;
	}

	try {
		const parsedUrl = new URL(url);
		parsedUrl.searchParams.set("campus", campusId);
		return parsedUrl.toString();
	} catch {
		return url;
	}
}

/**
 * Build a consultation (contact-us) URL with a campus query parameter.
 */
export function buildConsultationUrl(
	academyOrigin: string,
	campusId: string | null
): string {
	const baseUrl = academyOrigin.replace(/\/+$/, "");
	return appendCampusQueryParameter(`${baseUrl}/contact-us`, campusId);
}
